import React from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface DonutDataPoint {
  name: string;
  value: number;
}

interface LegendPosition {
  layout: "horizontal" | "vertical";
  verticalAlign: "top" | "middle" | "bottom";
  align: "left" | "center" | "right";
}

interface DonutChartComponentProps {
  data: DonutDataPoint[];
  title: string;
  height?: number;
  innerRadius?: string | number;
  outerRadius?: string | number;
  colors?: string[];
  legendPosition?: LegendPosition;
  showPercentage?: boolean;
  showLegend?: boolean;
}

export const transformDonutData = (
  data: Array<Record<string, any>>,
  columnName: string
): DonutDataPoint[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const cleanValues = data.map((item) =>
    item[columnName] === null || item[columnName] === ""
      ? "None"
      : String(item[columnName])
  );

  const counts: Record<string, number> = {};
  cleanValues.forEach((value) => {
    counts[value] = (counts[value] || 0) + 1;
  });

  return Object.entries(counts).map(([name, value]) => ({ name, value }));
};

const DonutChartComponent: React.FC<DonutChartComponentProps> = ({
  data,
  title,
  height = 500,
  innerRadius = "40%",
  outerRadius = "80%",
  colors = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#8dd1e1",
    "#a4de6c",
    "#d0ed57",
    "#83a6ed",
    "#8884d8",
    "#9cacf1",
    "#e99690",
    "#f58668",
  ],
  legendPosition = {
    layout: "horizontal",
    verticalAlign: "bottom",
    align: "center",
  },
  showPercentage = true,
  showLegend = true,
}) => {
  if (!data || data.length === 0) {
    return (
      <div>
        <h5>{title}</h5>
        <div>No Data Available</div>
      </div>
    );
  }

  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div>
      <h5>{title}</h5>
      <div style={{ width: "100%", height: `${height}px` }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={
                showPercentage
                  ? ({ percent }) =>
                      `${
                        percent > 0.01 ? (percent * 100).toFixed(1) + "%" : ""
                      }`
                  : ({ name }) => name
              }
              outerRadius={outerRadius}
              innerRadius={innerRadius}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colors[index % colors.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [
                `${value} (${((value / total) * 100).toFixed(1)}%)`,
              ]}
            />
            {showLegend && (
              <Legend
                layout={legendPosition.layout}
                verticalAlign={legendPosition.verticalAlign}
                align={legendPosition.align}
                wrapperStyle={{ paddingTop: 20 }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DonutChartComponent;
